import { useEffect, useState } from "react";
import { Loader2, Mail, Pencil, Plus, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { estApi, preconApi } from "../lib/api";
import { AddCustomer } from "./BoardCells";
import { ContactLines } from "@/components/ContactCell";
import EmailProposalDialog from "./EmailProposalDialog";

// Customer(s) on a bid: one line per company with its contact. Same directory dropdowns as the
// Bid Board grid, and the same records — the board, this tab and the proposal all stay in sync.
export default function BidCustomers({ job, estimateId, canEdit = true, onChanged }) {
  const inv = job.invitations || [];
  const [emailing, setEmailing] = useState(null);
  const drop = async (i) => {
    try { await preconApi.dropInvitation(job.id, i.id); toast.success(`${i.gc_name} removed`); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove that customer"); }
  };
  return (
    <div className="space-y-2" data-testid="bid-customers">
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
      {inv.map((i) => (
        <div key={i.id} className="flex items-center gap-3 py-2 text-xs" data-testid={`bid-customer-${i.id}`}>
          <span className="w-64 truncate font-medium text-slate-900 dark:text-slate-100">{i.gc_name}</span>
          <ContactSelect job={job} inv={i} disabled={!canEdit} onChanged={onChanged} />
          {estimateId && i.contact_email && (
            <Button variant="data" size="sm" className="h-6 shrink-0 px-2 text-[11px]" onClick={() => setEmailing(i)}
                    title={`Email the proposal to ${i.contact_email}`} data-testid={`bid-customer-send-${i.id}`}>
              <Mail className="mr-1 h-3 w-3" /> Send
            </Button>)}
          {!i.gc_contact_id && <span className="text-[11px] text-amber-600" data-testid={`bid-customer-nocontact-${i.id}`}>Needs a contact to email the proposal</span>}
          {canEdit && (
            <button type="button" onClick={() => drop(i)} title="Remove customer" data-testid={`bid-customer-remove-${i.id}`}
                    className="ml-auto rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"><X className="h-3.5 w-3.5" /></button>)}
        </div>))}
      </div>
      {!inv.length && <p className="text-[11px] text-slate-500">No customer on this bid yet.</p>}
      {canEdit && (
        <AddCustomer job={job} onChanged={onChanged} testid="bid-customers-add">
          <Button size="sm" className="h-8 text-xs" data-testid="bid-customers-add-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Customer</Button>
        </AddCustomer>)}
      {emailing && (
        <EmailProposalDialog open onOpenChange={(v) => !v && setEmailing(null)} estimateId={estimateId}
                             restrictEmails={[emailing.contact_email]} restrictLabel={emailing.contact_name || emailing.gc_name}
                             companyId={emailing.gc_company_id || ""} onSent={() => { setEmailing(null); onChanged?.(); }} />)}
    </div>
  );
}

function ContactSelect({ job, inv, disabled, onChanged }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ first_name: "", last_name: "", email: "" });
  const startEdit = () => {
    const [first_name = "", ...rest] = (inv.contact_name || "").split(" ");
    setDraft({ first_name, last_name: rest.join(" "), email: inv.contact_email || "" });
    setEditing(true); setAdding(true);
  };
  useEffect(() => {
    if (!open || list || !inv.gc_company_id) return;
    estApi.getContacts({ company_ids: [inv.gc_company_id], limit: 200 }).then((r) => setList(Array.isArray(r) ? r : r.contacts || [])).catch(() => setList([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = async (id) => {
    try { await preconApi.setInvitationContact(job.id, inv.id, id); setOpen(false); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not set the contact"); }
  };
  const create = async () => {
    try {
      if (editing) {
        await estApi.updateContact(inv.gc_contact_id, draft);
        await preconApi.setInvitationContact(job.id, inv.id, inv.gc_contact_id);
        toast.success("Contact updated");
        setList(null); setAdding(false); setEditing(false); setOpen(false); onChanged?.();
        return;
      }
      const c = await estApi.createContact({ ...draft, company_id: inv.gc_company_id, active: true });
      setList(null); setAdding(false); setDraft({ first_name: "", last_name: "", email: "" });
      await pick(c.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the contact"); }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setAdding(false); setEditing(false); } }}>
      <PopoverTrigger asChild>
        <div role="button" tabIndex={disabled ? -1 : 0} aria-disabled={disabled} data-testid={`bid-customer-contact-${inv.id}`}
             onClick={(e) => disabled && e.preventDefault()} title={disabled ? undefined : "Click to edit"}
             className={`group flex max-w-64 items-start gap-1 py-0.5 leading-tight ${disabled ? "pointer-events-none" : "cursor-pointer"}`}>
          {inv.gc_contact_id
            ? <ContactLines name={inv.contact_name} email={inv.contact_email} testid={`bid-customer-contact-lines-${inv.id}`} />
            : <span className="text-xs text-muted-foreground hover:text-foreground">— Add contact</span>}
          {!disabled && <Pencil className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />}
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" data-testid={`bid-customer-contact-pop-${inv.id}`}>
        {adding ? (
          <div className="space-y-1.5 p-2">
            {["first_name", "last_name", "email"].map((k) => (
              <input key={k} value={draft[k]} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                     placeholder={{ first_name: "First name", last_name: "Last name", email: "Email" }[k]}
                     className="h-7 w-full rounded border border-input bg-background px-2 text-xs" data-testid={`bid-customer-newcontact-${k}`} />))}
            <div className="flex justify-end gap-1.5 pt-1">
              <Button variant="nav" size="sm" className="h-7 text-xs" onClick={() => { setAdding(false); setEditing(false); }}>Cancel</Button>
              <Button size="sm" className="h-7 text-xs" disabled={!(draft.first_name || draft.last_name || draft.email)} onClick={create} data-testid="bid-customer-newcontact-save">{editing ? "Save" : "Add"}</Button>
            </div>
          </div>
        ) : (
          <Command>
            <CommandInput placeholder="Search..." className="h-8 text-xs" />
            <CommandList className="max-h-56">
              {!list ? <div className="flex items-center justify-center py-4 text-xs text-slate-500"><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Loading…</div>
                : <CommandEmpty className="py-3 text-center text-xs text-slate-500">No contacts at this company yet.</CommandEmpty>}
              <CommandGroup>
                {inv.gc_contact_id && <CommandItem value="__none" onSelect={() => pick("")} className="text-xs text-slate-500">No contact</CommandItem>}
                {(list || []).map((c) => (
                  <CommandItem key={c.id} value={`${c.first_name} ${c.last_name} ${c.email}`} onSelect={() => pick(c.id)} className="text-xs" data-testid={`bid-customer-contact-option-${c.id}`}>
                    <span className="min-w-0">
                      <span className="block truncate">{`${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email}</span>
                      {c.email && (c.first_name || c.last_name) && <span className="block truncate text-[10px] text-slate-500">{c.email}</span>}
                    </span>
                  </CommandItem>))}
              </CommandGroup>
            </CommandList>
            <div className="border-t border-border p-1">
              {inv.gc_contact_id && (
                <button type="button" onClick={startEdit} data-testid={`bid-customer-contact-edit-${inv.id}`}
                        className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"><Pencil className="h-3.5 w-3.5" /> Edit {inv.contact_name || "contact"}</button>)}
              <button type="button" onClick={() => setAdding(true)} data-testid={`bid-customer-contact-new-${inv.id}`}
                      className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-xs text-[#007BFF] hover:bg-slate-50 dark:hover:bg-slate-800"><UserPlus className="h-3.5 w-3.5" /> New contact</button>
            </div>
          </Command>)}
      </PopoverContent>
    </Popover>
  );
}
